import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useDeviceType } from '../hooks/useDeviceType';

const GalaxyBackground = () => {
  const { isMobile } = useDeviceType();

  // Generate orbiting planets with highly randomized properties
  const orbitingSpheres = useMemo(() => {
    // Reduce number of spheres on mobile
    const sphereCount = isMobile ? 4 : 12;
    
    return Array.from({ length: sphereCount }, (_, i) => {
      const orbitRadius = Math.random() * (isMobile ? 75 : 125) + 50;
      const size = Math.random() * (isMobile ? 10 : 15) + 5;
      const speed = Math.random() * (isMobile ? 30 : 40) + 20;
      const startAngle = Math.random() * 360;
      const direction = Math.random() < 0.5 ? 1 : -1;
      const orbitTilt = Math.random() * 30 - 15;
      const color = `rgba(${120 + Math.floor(Math.random() * 30)}, ${30 + Math.floor(Math.random() * 20)}, ${40 + Math.floor(Math.random() * 20)}, ${0.9 - (i % 4) * 0.15})`;

      return {
        id: i,
        size,
        orbitRadius,
        speed,
        startAngle,
        direction,
        orbitTilt,
        color,
      };
    });
  }, [isMobile]);

  // Generate background stars with randomized properties
  const stars = useMemo(() => {
    // Reduce number of stars on mobile
    const starCount = isMobile ? 15 : 30;
    
    return Array.from({ length: starCount }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 0.5,
      x: Math.random() * 100,
      y: Math.random() * 100,
      opacity: Math.random() * 0.6 + 0.1,
      twinkleSpeed: Math.random() * 5 + 1,
    }));
  }, [isMobile]);

  const prefersReducedMotion = useMemo(() => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Optimize animation performance for mobile
  const animationConfig = useMemo(() => ({
    duration: isMobile ? 6 : 4,
    scale: isMobile ? [1, 1.05, 1] : [1, 1.1, 1],
    opacity: isMobile ? [0.3, 0.4, 0.3] : [0.4, 0.6, 0.4],
  }), [isMobile]);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: -1 }}>
      {/* Background Stars - Reduced quantity and animation complexity on mobile */}
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full bg-accent"
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            boxShadow: `0 0 ${star.size * 2}px rgba(73, 17, 28, ${Math.random() * 0.5 + 0.3})`,
          }}
          animate={{
            opacity: [star.opacity, star.opacity + (isMobile ? 0.1 : 0.3), star.opacity],
            scale: isMobile ? [1, 1.2, 1] : [1, 1.5, 1],
          }}
          transition={{
            duration: star.twinkleSpeed * (isMobile ? 1.5 : 1),
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Central Sphere - Optimized animations for mobile */}
      <div className="absolute top-1/2 left-1/2 w-48 h-48 transform -translate-x-1/2 -translate-y-1/2" style={{ zIndex: 2 }}>
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle at 30% 30%, rgba(140, 35, 45, 0.95), rgba(73, 17, 28, 0.8) 40%, rgba(40, 10, 15, 0.7) 80%)',
            boxShadow: '0 0 60px rgba(73, 17, 28, 0.7)',
          }}
          animate={{
            boxShadow: [
              '0 0 60px rgba(73, 17, 28, 0.7)',
              `0 0 ${isMobile ? 70 : 80}px rgba(73, 17, 28, ${isMobile ? 0.8 : 0.9})`,
              '0 0 60px rgba(73, 17, 28, 0.7)',
            ],
          }}
          transition={{
            duration: animationConfig.duration,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <motion.div
            className="absolute inset-[-20px] rounded-full opacity-50 blur-xl"
            style={{
              background: 'radial-gradient(circle, rgba(73, 17, 28, 0.6), transparent 70%)',
            }}
            animate={{
              scale: animationConfig.scale,
              opacity: animationConfig.opacity,
            }}
            transition={{
              duration: animationConfig.duration,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.div>
      </div>

      {/* Orbiting Planets - Conditionally rendered and optimized for mobile */}
      {!prefersReducedMotion && orbitingSpheres.map((sphere) => (
        <motion.div
          key={sphere.id}
          className="absolute top-1/2 left-1/2"
          style={{
            transform: `translate(-50%, -50%) rotate(${sphere.startAngle}deg)`,
            transformOrigin: 'center',
          }}
          animate={{
            rotate: [sphere.startAngle, sphere.startAngle + 360 * sphere.direction],
          }}
          transition={{
            duration: sphere.speed * (isMobile ? 1.2 : 1),
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <div
            className="absolute"
            style={{
              left: 0,
              transform: `translateX(${sphere.orbitRadius}px) rotate(${Math.random() * 10 - 5}deg)`,
            }}
          >
            <motion.div
              className="rounded-full"
              style={{
                width: sphere.size,
                height: sphere.size,
                background: `radial-gradient(circle at 30% 30%, ${sphere.color}, rgba(73, 17, 28, 0.6) 70%)`,
                boxShadow: `0 0 ${15 + Math.random() * 10}px rgba(73, 17, 28, ${0.6 + Math.random() * 0.2})`,
              }}
              animate={{
                scale: isMobile ? [1, 1.05, 1] : [1, 1.1, 1],
                opacity: isMobile ? [0.8, 0.9, 0.8] : [0.8, 1, 0.8],
              }}
              transition={{
                duration: isMobile ? 4 : 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 70% 70%, transparent, rgba(0, 0, 0, 0.4))',
                  transform: 'scale(0.9)',
                }}
              />
            </motion.div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default GalaxyBackground;